import { useEffect } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { logoutOneSignalIdentity } from "@/hooks/use-onesignal-identity";
import { clearReturnTo } from "@/lib/auth-return-to";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LogOut } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { usePageTitle } from "@/hooks/use-page-title";
import { resetAppAnalytics } from "@/lib/app-analytics";

export default function AppAwaitingApproval() {
  const queryClient = useQueryClient();
  usePageTitle("Awaiting Approval");
  const [, navigate] = useLocation();

  // Self-serve signup: nobody new should ever land here (B13 — the legacy
  // "Founding Waitlist" screen is gone). Any approved account, whatever its
  // status, goes straight to the dashboard. Only an account whose approval
  // was revoked stays here, because the app shell sends unapproved accounts
  // back to this route and redirecting them would loop.
  const meQuery = useQuery<{ user?: { approved?: boolean; status?: string } }>({
    queryKey: ["/api/app/auth/me"],
  });
  const me = meQuery.data?.user;
  useEffect(() => {
    if (me?.approved) {
      navigate("/app/dashboard", { replace: true });
    }
  }, [me?.approved, navigate]);

  // Active-but-unapproved = an admin revoked this account's access
  // ("Revoke approval"). Show a clear blocked state, not "awaiting approval".
  const isRevoked = me != null && me.status === "active" && me.approved !== true;

  const logoutMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/auth/logout", {});
      void resetAppAnalytics();
    },
    onSuccess: () => {
      logoutOneSignalIdentity();
      clearReturnTo();
      queryClient.clear();
      navigate("/app/login");
    },
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-awaiting-logo" />
        </div>

        <Card className="w-full p-8 text-center shadow-xl border border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-brand/10 flex items-center justify-center">
              <LogOut className="h-8 w-8 text-brand" />
            </div>
          </div>
          {isRevoked ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="text-awaiting-title">Account access revoked</h1>
              <p className="text-gray-500 text-sm mb-6" data-testid="text-awaiting-message">
                Access to this account has been revoked by an administrator, so the app is unavailable.
              </p>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
                <p className="text-sm text-red-600">
                  If you believe this is a mistake, contact <a href="mailto:support@padpulse.io" className="font-medium underline">support@padpulse.io</a>.
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="text-awaiting-title">Your account isn't active yet</h1>
              <p className="text-gray-500 text-sm mb-6" data-testid="text-awaiting-message">
                Sign in again to finish setting up, or contact us and we'll get you in.
              </p>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-600">
                  Questions? Reach out to <a href="mailto:support@padpulse.io" className="font-medium underline">support@padpulse.io</a>.
                </p>
              </div>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
            className="text-gray-500 hover:text-gray-700"
            data-testid="button-awaiting-logout"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Back to login
          </Button>
        </Card>
      </div>
    </div>
  );
}
